import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../store';
import {
	setPeriodWeeksLoading,
	setPeriodWeeksData,
	setCurrentFiscalContext,
	setPeriodWeeksError,
	resetPeriodWeeks,
} from '../../../store/slices/analyticsHubSlice';
import { fetchPeriodWeeks } from '../../../api/periodWeeksApi';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DropdownItem {
	id: string | number;
	displayText: string;
}

interface UsePeriodWeeksReturn {
	/** Period dropdown options derived from selected years (e.g. [{id:'P1', displayText:'P1'}, ...]) */
	periodOptions: DropdownItem[];
	/** Week dropdown options derived from selected periods (e.g. [{id:'W1', displayText:'W1'}, ...]) */
	weekOptions: DropdownItem[];
	/** Whether the API call is in progress */
	loading: boolean;
	/** Error message if the fetch failed */
	error: string | null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Custom hook that fetches period/week data when selected years change,
 * and derives cascading dropdown options for Period and Week In Year.
 *
 * Cascading logic:
 *   Year selection → triggers API fetch → populates periods
 *   Period selection → filters weeks from already-fetched data
 *
 * @param selectedYears - Array of selected year numbers (from YEAR dropdown)
 * @param selectedPeriods - Array of selected period numbers (from PERIOD dropdown)
 */
export function usePeriodWeeks(
	selectedYears: number[],
	selectedPeriods: number[] = []
): UsePeriodWeeksReturn {
	const dispatch = useAppDispatch();
	const abortControllerRef = useRef<AbortController | null>(null);
	const prevYearsRef = useRef<string>('');

	// Selectors
	const { yearData, loading, error } = useSelector(
		(state: RootState) => state.analyticsHub.periodWeeks
	);

	/**
	 * Fetch period-weeks from API when selected years change.
	 */
	const fetchData = useCallback(
		async (years: number[]) => {
			if (years.length === 0) {
				dispatch(resetPeriodWeeks());
				return;
			}

			// Cancel previous in-flight request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			abortControllerRef.current = new AbortController();

			dispatch(setPeriodWeeksLoading());

			try {
				const response = await fetchPeriodWeeks(years);
				dispatch(setPeriodWeeksData(response.data));
				// Store the current fiscal context if provided by the API
				if (response.currentFiscalContext) {
					dispatch(setCurrentFiscalContext(response.currentFiscalContext));
				}
			} catch (err: any) {
				if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
					return;
				}
				dispatch(setPeriodWeeksError(err.message || 'Failed to fetch period-weeks'));
			}
		},
		[dispatch]
	);

	/**
	 * Effect: trigger fetch when selectedYears changes.
	 * Uses a serialized string comparison to avoid unnecessary re-fetches.
	 */
	useEffect(() => {
		const sortedYears = [...selectedYears].sort();
		const yearsKey = sortedYears.join(',');

		if (yearsKey === prevYearsRef.current) {
			return; // No change — skip
		}

		prevYearsRef.current = yearsKey;
		fetchData(sortedYears);
	}, [selectedYears, fetchData]);

	/**
	 * Cleanup on unmount: abort in-flight request.
	 */
	useEffect(() => {
		return () => {
			abortControllerRef.current?.abort();
		};
	}, []);

	// ─── Derived dropdown options ─────────────────────────────────────────────

	/**
	 * Period options: all unique period numbers across all fetched year data.
	 */
	const periodOptions: DropdownItem[] = useMemo(() => {
		if (!yearData || yearData.length === 0) return [];

		const periodSet = new Set<number>();
		for (const yd of yearData) {
			for (const pd of yd.periods) {
				periodSet.add(pd.period);
			}
		}

		return Array.from(periodSet)
			.sort((a, b) => a - b)
			.map((p) => ({
				id: `P${p}`,
				displayText: `P${p}`,
			}));
	}, [yearData]);

	/**
	 * Week options: derived from the selected periods within the fetched year data.
	 * If no period is selected, shows all unique weeks across all periods.
	 *
	 * id uses weekInYear (for API payloads), displayText uses week (week within period).
	 */
	const weekOptions: DropdownItem[] = useMemo(() => {
		if (!yearData || yearData.length === 0) return [];

		// Map weekInYear → { week, period } to preserve both values and show period context
		const weekMap = new Map<number, { week: number; period: number }>();

		for (const yd of yearData) {
			for (const pd of yd.periods) {
				// If specific periods are selected, only include weeks for those periods
				if (selectedPeriods.length > 0 && !selectedPeriods.includes(pd.period)) {
					continue;
				}
				for (const weekObj of pd.weeks) {
					weekMap.set(weekObj.weekInYear, { week: weekObj.week, period: pd.period });
				}
			}
		}

		return Array.from(weekMap.entries())
			.sort((a, b) => a[0] - b[0])
			.map(([weekInYear, { week, period }]) => ({
				id: `W${weekInYear}`,
				displayText: `W${weekInYear} (P${period}W${week})`,
			}));
	}, [yearData, selectedPeriods]);

	return {
		periodOptions,
		weekOptions,
		loading,
		error,
	};
}
